import { Close, Info, Warning } from '@mui/icons-material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeIcon from '@mui/icons-material/Merge';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, ButtonGroup, Divider, Fab, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { createRef, useEffect, useState } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { reportError } from './ErrorReporter.js';
import { buildArraySchemaWarningPath, buildFieldPaths, chronologyTreatmentStartSortMs, getFormatFunction, getJSONPathCompatibleKey, getMedicalChronologyConsolidationState, renderTreatmentDates } from './PathUtils.js';

/**
 * Builds { textPath, needsReviewPath, caveatsPath } from any JSON base path + field name.
 * All per-schema path builders (event detail, provider, consolidated, etc.) are special cases of this.
 */
export function buildWarningPaths(base, fieldName) {
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
}

/**
 * Mirrors backend hasIncidentSummary in llm_processor.js: case-level summary is
 * considered usable iff its incident_date parses to a valid Date.
 */
function hasIncidentSummary(incidentSummary) {
    const date = incidentSummary?.incident_date;
    return !!(date && !isNaN(new Date(date).getTime()));
}

/** Maps medical chronology detailed_narrative item_type to UI labels. */
function getNarrativeItemTypeLabel(itemType) {
    const labelMap = {
        subjective_complaints: 'Complaints',
        objective_findings: 'Clinical Findings',
        assessment_diagnosis: 'Diagnosis',
        plan_treatment_rendered: 'Treatment',
        future_recommendations: 'Recommendations',
        causation_statement: 'Causation Statement',
        pre_existing_conditions_mentioned: 'Pre-existing Conditions',
        work_status_disability: 'Work Status'
    };
    return labelMap[itemType] || itemType;
}
export default function MedicalChronology({ caseInfo, setCaseInfo, fetchCaseInfoAndUpdateUI, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    const summaryTableRef = createRef();
    const incidentSummaryTableRef = createRef();
    // TODO: When hasConsolidated goes from false to true, animate the appearance of the button group make it noticable.
    const { hasConsolidated, unconsolidatedFiles, hasUnconsolidated } = getMedicalChronologyConsolidationState(caseInfo);
    const [showConsolidatedOnly, setShowConsolidatedOnly] = useState(hasConsolidated);
    const [auditExpanded, setAuditExpanded] = useState(false);
    const [auditPage, setAuditPage] = useState(1);
    const [expandedSummaryReports, setExpandedSummaryReports] = useState({});
    const [headerOnlySummaryReports, setHeaderOnlySummaryReports] = useState({});
    const collapsedSummaryHeight = 200;
    const isMedicalRecordsInProgress = caseInfo && caseInfo['documents'] && Object.values(caseInfo['documents']).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('medical_records') || doc['analysis_queue']?.includes('personal_injury_catchall'));
    const isConsolidationInProgress = caseInfo && caseInfo['analysis_queue']?.[0]?.includes('medical_reports_');
    const isProcessingInProgress = caseInfo && (caseInfo['analysis_queue']?.length > 0 ||
        Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.length > 0));
    assert('caseId' in caseInfo, 'caseId not found in caseInfo');

    // Auto-switch to consolidated view when consolidation completes
    useEffect(() => {
        if (hasConsolidated) {
            setShowConsolidatedOnly(true);
        }
    }, [hasConsolidated]);

    function getSelectedDocumentKeys() {
        return caseInfo !== null && 'documents' in caseInfo
            ? Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap?.[caseInfo['documents'][docKey]?.['filename']]?.['selected'])
            : [];
    }

    function getExcludedAuditEntriesForDocuments(documentKeys) {
        const entries = [];
        documentKeys.forEach((documentKey) => {
            const audit = caseInfo?.documents?.[documentKey]?.analysis?.audit;
            (audit || []).forEach((chunk, auditChunkIndex) => {
                const allNamedPersons = chunk?.all_named_persons || [];
                allNamedPersons.forEach((person, personIndex) => {
                    if (!person || person.present_in_medical_chronology !== false) return;
                    if (person.dismissed_by_user === true) return;
                    entries.push({
                        documentKey,
                        auditChunkIndex,
                        personIndex,
                        person_name: person.person_name || 'Unknown',
                        reason: person.reason || 'No reason provided.',
                        reason_justifies_exclusion: person.reason_justifies_exclusion === true,
                        page: person.page || 1,
                    });
                });
            });
        });
        return entries;
    }

    async function persistCaseFieldUpdates(updates, successMessage, errorMessage) {
        try {
            const response = await fetch(`/api/case/${caseInfo.caseId}/field`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            if (!response.ok) {
                console.error('Error persisting audit updates:', response.statusText);
                await reportError(
                    new Error(`PATCH /api/case/${caseInfo.caseId}/field failed: ${response.status} ${response.statusText}`),
                    'MedicalChronology.persistCaseFieldUpdates',
                    { caseId: caseInfo.caseId, pathCount: Object.keys(updates).length }
                );
                setSnackBarMessage(errorMessage);
                return false;
            }
            setSnackBarMessage(successMessage);
            return true;
        } catch (error) {
            console.error('Error persisting audit updates:', error);
            await reportError(error, 'MedicalChronology.persistCaseFieldUpdates', { caseId: caseInfo.caseId });
            setSnackBarMessage(errorMessage);
            return false;
        }
    }

    function setAuditEntriesDismissedInCaseInfo(entriesToDismiss) {
        const updatedCaseInfo = JSON.parse(JSON.stringify(caseInfo));
        entriesToDismiss.forEach(({ documentKey, auditChunkIndex, personIndex }) => {
            const audit = updatedCaseInfo?.documents?.[documentKey]?.analysis?.audit || [];
            const chunk = audit?.[auditChunkIndex];
            const arr = chunk?.all_named_persons;
            if (!Array.isArray(arr) || personIndex < 0 || personIndex >= arr.length) return;
            arr[personIndex] = { ...arr[personIndex], dismissed_by_user: true };
        });
        setCaseInfo(updatedCaseInfo);
    }

    function buildAuditDismissUpdates(entries) {
        const documentKeyToChunkToIndexes = {};
        entries.forEach((e) => {
            if (!documentKeyToChunkToIndexes[e.documentKey]) documentKeyToChunkToIndexes[e.documentKey] = {};
            const chunkKey = e.auditChunkIndex;
            if (!documentKeyToChunkToIndexes[e.documentKey][chunkKey]) documentKeyToChunkToIndexes[e.documentKey][chunkKey] = [];
            documentKeyToChunkToIndexes[e.documentKey][chunkKey].push(e.personIndex);
        });
        const updates = {};
        Object.entries(documentKeyToChunkToIndexes).forEach(([documentKey, chunkMap]) => {
            const audit = caseInfo?.documents?.[documentKey]?.analysis?.audit || [];
            Object.entries(chunkMap).forEach(([chunkKey, indexes]) => {
                const auditChunkIndex = Number(chunkKey);
                const current = audit?.[auditChunkIndex]?.all_named_persons || [];
                const indexSet = new Set(indexes);
                const next = current.map((person, idx) =>
                    indexSet.has(idx) && person && typeof person === 'object'
                        ? { ...person, dismissed_by_user: true }
                        : person
                );
                updates[`$.documents['${documentKey}'].analysis.audit[${auditChunkIndex}].all_named_persons`] = next;
            });
        });
        return updates;
    }

    async function dismissSingleAuditEntry(entry) {
        const audit = caseInfo?.documents?.[entry.documentKey]?.analysis?.audit;
        const arr = (audit || [])?.[entry.auditChunkIndex]?.all_named_persons;
        const current = Array.isArray(arr) && arr[entry.personIndex] ? arr[entry.personIndex] : null;
        const path = `$.documents['${entry.documentKey}'].analysis.audit[${entry.auditChunkIndex}].all_named_persons[${entry.personIndex}]`;
        if (!current || typeof current !== 'object') return;
        const updates = { [path]: { ...current, dismissed_by_user: true } };
        const ok = await persistCaseFieldUpdates(
            updates,
            'Dismissed 1 audit entry.',
            'Error dismissing audit entry.'
        );
        if (!ok) return;
        setAuditEntriesDismissedInCaseInfo([entry]);
    }

    async function dismissAllAuditEntries(entries) {
        const updates = buildAuditDismissUpdates(entries);
        const ok = await persistCaseFieldUpdates(
            updates,
            'Dismissed all audit entries.',
            'Error dismissing audit entries.'
        );
        if (!ok) return;
        setAuditEntriesDismissedInCaseInfo(entries);
        setAuditPage(1);
        setAuditExpanded(false);
    }

    function renderAuditWidgetForAllRecords() {
        const selectedKeys = getSelectedDocumentKeys();
        let entries = getExcludedAuditEntriesForDocuments(selectedKeys);
        // Show entries with reason_justifies_exclusion === false first
        entries = [...entries].sort((a, b) => (a.reason_justifies_exclusion === b.reason_justifies_exclusion) ? 0 : (a.reason_justifies_exclusion ? 1 : -1));

        // Avoid overwhelming the user if there are no missing personnel.
        if (entries.length === 0) return null;

        const notJustifiedCount = entries.filter(e => e.reason_justifies_exclusion === false).length;
        const isErrorState = notJustifiedCount > 0;

        const previewCount = 2;
        const pageSize = 10;
        const totalPages = Math.max(1, Math.ceil(entries.length / pageSize));
        const safePage = Math.min(Math.max(1, auditPage), totalPages);
        const pageStart = (safePage - 1) * pageSize;
        const pageEntries = entries.slice(pageStart, pageStart + pageSize);
        const previewEntries = entries.slice(0, previewCount);

        const title = 'Audit Results';
        const summaryText = isErrorState
            ? `${notJustifiedCount} named individuals in the document do not appear in the chronology and might indicate errors. Please review manually:`
            : `${entries.length} named individuals in the document were intentionally not included in the medical chronology. You can manually verify to ensure correctness:`;

        const tone = isErrorState ? {
            icon: <Warning sx={{ color: 'var(--error-color)', fontSize: 18 }} />,
            borderColor: 'rgba(244, 67, 54, 0.25)',
            backgroundColor: 'rgba(244, 67, 54, 0.03)',
        } : {
            icon: <Info sx={{ color: 'var(--info-color)', fontSize: 18 }} />,
            borderColor: 'rgba(33, 150, 243, 0.25)',
            backgroundColor: 'rgba(33, 150, 243, 0.03)',
        };

        const renderEntryRow = (entry, idx) => {
            const rowIsError = entry.reason_justifies_exclusion === false;
            const rowTone = rowIsError ? {
                icon: <Warning sx={{ color: 'var(--error-color)', fontSize: 14, flexShrink: 0 }} />,
                bg: 'rgba(244, 67, 54, 0.05)',
                border: 'rgba(244, 67, 54, 0.2)',
                color: 'var(--error-color)',
            } : {
                icon: <Info sx={{ color: 'var(--info-color)', fontSize: 14, flexShrink: 0 }} />,
                bg: 'rgba(33, 150, 243, 0.05)',
                border: 'rgba(33, 150, 243, 0.2)',
                color: 'var(--info-color)',
            };

            return (
                <Box
                    key={`${entry.documentKey}-${entry.auditChunkIndex ?? 'obj'}-${entry.personIndex}-${idx}`}
                    onClick={() => {
                        setDisplayedDocument({ key: entry.documentKey, page: entry.page || 1 });
                    }}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 0.75,
                        px: 1,
                        py: 0.75,
                        borderRadius: '6px',
                        backgroundColor: rowTone.bg,
                        border: `1px solid ${rowTone.border}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 'var(--shadow-subtle)',
                            '& .audit-clear-button': { opacity: 1, visibility: 'visible' }
                        }
                    }}
                >
                    {rowTone.icon}
                    <Typography
                        sx={{
                            color: rowTone.color,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            mr: 0.25,
                            flexShrink: 0,
                            maxWidth: '260px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={entry.person_name}
                    >
                        {entry.person_name}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: '0.85rem',
                            flex: 1,
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: auditExpanded ? 'normal' : 'nowrap'
                        }}
                        title={entry.reason}
                    >
                        {entry.reason}
                    </Typography>
                    <IconButton
                        className="audit-clear-button"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            dismissSingleAuditEntry(entry);
                        }}
                        sx={{
                            color: rowTone.color,
                            padding: '2px',
                            opacity: 0,
                            visibility: 'hidden',
                            transition: 'all 0.2s ease',
                            backgroundColor: rowIsError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '50%',
                            width: 22,
                            height: 22,
                            '&:hover': {
                                backgroundColor: rowIsError ? 'rgba(244, 67, 54, 0.18)' : 'rgba(33, 150, 243, 0.18)',
                                transform: 'translateY(-1px)',
                                boxShadow: 'var(--shadow-subtle)',
                            }
                        }}
                        aria-label="Dismiss audit entry"
                    >
                        <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            );
        };

        return (
            <Box
                sx={{
                    mb: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: '10px',
                    border: `1px solid ${tone.borderColor}`,
                    backgroundColor: tone.backgroundColor,
                    boxShadow: 'var(--shadow-light)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {tone.icon}
                        <Typography sx={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                            {title}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                                setAuditExpanded(!auditExpanded);
                                setAuditPage(1);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {auditExpanded ? 'Show less' : 'See All'}
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => dismissAllAuditEntries(entries)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                color: isErrorState ? 'var(--error-color)' : 'var(--info-color)'
                            }}
                        >
                            Dismiss All
                        </Button>
                    </Box>
                </Box>

                <Typography sx={{ mt: 0.75, mb: 1.25, color: 'var(--text-primary)', fontSize: '0.88rem', lineHeight: 1.35 }}>
                    {summaryText}
                </Typography>

                <Box textAlign='left'>
                    {(auditExpanded ? pageEntries : previewEntries).map(renderEntryRow)}
                </Box>

                {auditExpanded && entries.length > pageSize && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={safePage <= 1}
                            onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                            sx={{ textTransform: 'none' }}
                        >
                            Previous
                        </Button>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                            Page {safePage} of {totalPages}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={safePage >= totalPages}
                            onClick={() => setAuditPage(p => Math.min(totalPages, p + 1))}
                            sx={{ textTransform: 'none' }}
                        >
                            Next
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header section with ButtonGroup on left and CopyToClipboardButton on right */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {hasConsolidated && (
                        <ButtonGroup
                            sx={{
                                visibility: hasConsolidated ? "visible" : "hidden",
                                pointerEvents: "auto"
                            }}
                            aria-label="view mode button group"
                            variant="text"
                            size="small"
                            className="selectConsolidatedButtonGroup"
                        >
                            <Button
                                onClick={() => setShowConsolidatedOnly(false)}
                                disabled={!showConsolidatedOnly}
                                key="all"
                                size="small"
                            >
                                All Records
                            </Button>
                            <Button
                                onClick={() => setShowConsolidatedOnly(true)}
                                disabled={showConsolidatedOnly}
                                key="consolidated"
                                size="small"
                            >
                                Consolidated Records
                            </Button>
                        </ButtonGroup>
                    )}
                    {isMedicalRecordsInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Medical Chronology is being extracted...
                            </Typography>
                        </Box>
                    )}
                    {!isMedicalRecordsInProgress && isConsolidationInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle",
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Consolidating medical records...
                            </Typography>
                        </Box>
                    )}
                    {((hasConsolidated && hasUnconsolidated) || (unconsolidatedFiles.length > 1)) && !isProcessingInProgress && (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)'
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--info-color)',
                                }}
                            >
                                {hasConsolidated && hasUnconsolidated
                                    ? `Click Consolidate to incorporate new data extracted from ${unconsolidatedFiles.length} files`
                                    : 'Raw information extracted from files below. Click Consolidate to generate a single, master medical report.'
                                }
                            </Typography>
                            <Fab
                                onClick={async () => {
                                    const response = await fetch('/api/case/' + caseInfo['caseId'] + '/analysis_queue', {
                                        method: 'POST',
                                        credentials: 'same-origin',
                                        headers: new Headers({ 'content-type': 'application/json' }),
                                        body: JSON.stringify({
                                            analysis_queue: ['medical_reports_canonicalize'],
                                            item_type: 'consolidation'
                                        })
                                    });
                                    console.log('Consolidate button clicked: ' + response);

                                    if (response.ok) {
                                        setCaseInfo({ ...caseInfo, analysis_queue: [...caseInfo['analysis_queue'] || [], 'medical_reports_canonicalize'] });
                                        setTimeout(fetchCaseInfoAndUpdateUI, 10000);
                                    }
                                }}
                                variant="extended"
                                size="medium"
                                color="primary"
                                sx={{
                                    top: "-1px",
                                    background: 'var(--button-gradient)',
                                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    padding: '14px 24px',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: 'none',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                        background: 'var(--button-gradient-hover)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        // background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                        transition: 'left 0.6s'
                                    },
                                    '&:hover::before': {
                                        left: '100%'
                                    }
                                }}
                            >
                                <MergeIcon sx={{ mr: 1 }} style={{ transform: 'rotate(90deg)' }} />
                                Consolidate
                            </Fab>
                        </Box>
                    )}
                </Box>
                {!showConsolidatedOnly && <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Chronology" />}
            </Box>

            {/* Content section below the header */}
            {showConsolidatedOnly ? consolidatedMedicalReports() : medicalChronology()}
        </Box >
    );

    function consolidatedMedicalReports() {
        const medicalReports = caseInfo?.['analysis']?.['medical_reports'] || [];
        const summaryTableSx = {
            backgroundColor: 'var(--background-color)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            boxShadow: 'var(--shadow-light)'
        };

        if (medicalReports.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        Consolidated medical reports will appear here after consolidation is complete.
                    </Typography>
                </Box>
            );
        }

        const incidentSummaryBase = '$.analysis.incident_summary';
        const incidentSummary = caseInfo?.analysis?.incident_summary || {};
        const incidentSummarySources = incidentSummary.sources || [];
        const hasValidIncidentSummary = hasIncidentSummary(incidentSummary);

        let pickedIncidentDate = null;
        let pickedIncidentDescription = null;
        if (!hasValidIncidentSummary) {
            const incidentDetailsCandidates = [];
            getSelectedDocumentKeys().forEach((documentKey) => {
                const arr = caseInfo.documents?.[documentKey]?.analysis?.incident_details;
                if (!Array.isArray(arr)) return;
                arr.forEach((item, itemIndex) => {
                    incidentDetailsCandidates.push({ item, documentKey, itemIndex });
                });
            });
            const sortByIncidentDateAsc = (a, b) => {
                const t1 = Date.parse(a.item.incident_date);
                const t2 = Date.parse(b.item.incident_date);
                return (Number.isNaN(t1) ? Infinity : t1) - (Number.isNaN(t2) ? Infinity : t2);
            };
            const withIncidentDate = incidentDetailsCandidates.filter(
                (c) => c.item.incident_date && String(c.item.incident_date).trim().length > 0
            );
            withIncidentDate.sort(sortByIncidentDateAsc);
            pickedIncidentDate = withIncidentDate[0] || null;

            const withIncidentDescription = incidentDetailsCandidates.filter(
                (c) => c.item.incident_description && String(c.item.incident_description).trim().length > 0
            );
            withIncidentDescription.sort(sortByIncidentDateAsc);
            if (pickedIncidentDate) {
                pickedIncidentDescription = withIncidentDescription.find(
                    (c) => c.documentKey === pickedIncidentDate.documentKey && c.itemIndex === pickedIncidentDate.itemIndex
                ) || null;
            }
            if (!pickedIncidentDescription) {
                pickedIncidentDescription = withIncidentDescription[0] || null;
            }
        }

        const pickedIncidentDateValid = !!(pickedIncidentDate && !isNaN(new Date(pickedIncidentDate.item.incident_date).getTime()));
        const hasUsableIncidentDateForReportSummary = hasValidIncidentSummary || pickedIncidentDateValid;

        const incidentDateFieldPaths = hasValidIncidentSummary
            ? buildWarningPaths(incidentSummaryBase, 'incident_date')
            : pickedIncidentDate
                ? buildWarningPaths(`$.documents['${pickedIncidentDate.documentKey}'].analysis.incident_details[${pickedIncidentDate.itemIndex}]`, 'incident_date')
                : buildWarningPaths(incidentSummaryBase, 'incident_date');
        const incidentDateSources = hasValidIncidentSummary
            ? incidentSummarySources
            : pickedIncidentDate
                ? [{ filename: caseInfo.documents?.[pickedIncidentDate.documentKey]?.filename, page: 1 }]
                : incidentSummarySources;

        const incidentDescriptionFieldPaths = hasValidIncidentSummary
            ? buildWarningPaths(incidentSummaryBase, 'incident_description')
            : pickedIncidentDescription
                ? buildWarningPaths(`$.documents['${pickedIncidentDescription.documentKey}'].analysis.incident_details[${pickedIncidentDescription.itemIndex}]`, 'incident_description')
                : buildWarningPaths(incidentSummaryBase, 'incident_description');
        const incidentDescriptionSources = hasValidIncidentSummary
            ? incidentSummarySources
            : pickedIncidentDescription
                ? [{ filename: caseInfo.documents?.[pickedIncidentDescription.documentKey]?.filename, page: 1 }]
                : incidentSummarySources;

        const incidentSummaryRows = [
            {
                description: 'Incident Date',
                placeholder: 'Not found',
                sources: incidentDateSources,
                fieldPaths: incidentDateFieldPaths,
                isFirstInGroup: true,
                groupCount: 1
            },
            {
                description: 'Incident Description',
                placeholder: 'Not found',
                sources: incidentDescriptionSources,
                fieldPaths: incidentDescriptionFieldPaths,
                isFirstInGroup: true,
                groupCount: 1
            }
        ];

        return (
            <Box sx={{ mt: 2 }}>
                {hasUsableIncidentDateForReportSummary && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                Incident
                            </Typography>
                            <CopyToClipboardButton nodeRef={incidentSummaryTableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy incident summary" />
                        </Box>
                        <Table ref={incidentSummaryTableRef} sx={summaryTableSx}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        color: 'var(--text-primary)',
                                        borderBottom: '2px solid var(--border-color)',
                                        minWidth: '200px'
                                    }}>
                                        Description
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        color: 'var(--text-primary)',
                                        borderBottom: '2px solid var(--border-color)',
                                        minWidth: '300px'
                                    }}>
                                        Details
                                    </TableCell>
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        color: 'var(--text-primary)',
                                        borderBottom: '2px solid var(--border-color)',
                                        minWidth: '200px'
                                    }}>
                                        Source Document
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {renderSummaryTableDataRows(incidentSummaryRows)}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                {medicalReports.map((report, reportIndex) => {
                    const summaryRows = getSummaryTableRows(report, reportIndex);
                    const reportSummaryKey = report.uuid || reportIndex;
                    const isSummaryExpanded = expandedSummaryReports[reportSummaryKey] === true;
                    const isSummaryHeaderOnly = headerOnlySummaryReports[reportSummaryKey] === true;
                    const canExpandSummary = summaryRows.length > 3;
                    const shouldHideSummaryBody = canExpandSummary && !isSummaryExpanded && isSummaryHeaderOnly;

                    return (
                        <Accordion
                            key={report.uuid || reportIndex}
                            defaultExpanded={reportIndex === 0}
                            sx={{
                                mb: 2,
                                border: '1px solid var(--border-color)',
                                boxShadow: 'var(--shadow-light)',
                                '&:before': { display: 'none' },
                                backgroundColor: 'var(--background-color)'
                            }}
                        >
                            <AccordionSummary
                                // Make only icon clickable to expand/contract Accordian from https://stackoverflow.com/questions/69358781/material-ui-expand-accordion-by-clicking-the-icon-only
                                sx={{
                                    pointerEvents: "none",
                                    backgroundColor: 'var(--surface-color)',
                                    borderBottom: '1px solid var(--border-color)',
                                    height: '64px', // Without this, height of accordian summary changes when expanded or collapsed due to minHeigh rule only applied on expanded by library.
                                    padding: '8px 16px',
                                    '&:hover': {
                                        backgroundColor: 'var(--surface-color)'
                                    }
                                }}
                                expandIcon={<ExpandMoreIcon
                                    sx={{
                                        pointerEvents: "auto",
                                        color: 'var(--text-secondary)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            color: 'var(--accent-color)',
                                            transform: 'scale(1.1)',
                                        }
                                    }}
                                // onClick={onAccordianIconClicked}
                                />}
                                style={{
                                    flexDirection: "row-reverse",
                                    cursor: "default",
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Medical Report for {report.patient_name || 'Unknown Patient'}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 3 }}>
                                {hasSummaryTableRows(report) && (
                                    <>
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 2
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                                    Summary
                                                </Typography>
                                                {canExpandSummary && (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        endIcon={isSummaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        onClick={() => {
                                                            if (isSummaryExpanded) {
                                                                setExpandedSummaryReports((prev) => ({
                                                                    ...prev,
                                                                    [reportSummaryKey]: false
                                                                }));
                                                                setHeaderOnlySummaryReports((prev) => ({
                                                                    ...prev,
                                                                    [reportSummaryKey]: true
                                                                }));
                                                            } else {
                                                                setExpandedSummaryReports((prev) => ({
                                                                    ...prev,
                                                                    [reportSummaryKey]: true
                                                                }));
                                                                setHeaderOnlySummaryReports((prev) => ({
                                                                    ...prev,
                                                                    [reportSummaryKey]: false
                                                                }));
                                                            }
                                                        }}
                                                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto' }}
                                                    >
                                                        {isSummaryExpanded ? 'Hide summary' : 'See full summary'}
                                                    </Button>
                                                )}
                                            </Box>
                                            <CopyToClipboardButton nodeRef={summaryTableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Summary" />
                                        </Box>
                                        {!shouldHideSummaryBody && (
                                            <Box
                                                sx={{
                                                    maxHeight: (!isSummaryExpanded && canExpandSummary) ? `${collapsedSummaryHeight}px` : 'none',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <Table
                                                    ref={summaryTableRef}
                                                    sx={summaryTableSx}
                                                >
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                                                            <TableCell sx={{
                                                                fontWeight: 600,
                                                                color: 'var(--text-primary)',
                                                                borderBottom: '2px solid var(--border-color)',
                                                                minWidth: "200px"
                                                            }}>
                                                                Description
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                fontWeight: 600,
                                                                color: 'var(--text-primary)',
                                                                borderBottom: '2px solid var(--border-color)',
                                                                minWidth: "300px"
                                                            }}>
                                                                Details
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                fontWeight: 600,
                                                                color: 'var(--text-primary)',
                                                                borderBottom: '2px solid var(--border-color)',
                                                                minWidth: "200px"
                                                            }}>
                                                                Source Document
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>

                                                    <TableBody>
                                                        {renderSummaryTableDataRows(summaryRows)}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        )}
                                        {canExpandSummary && !shouldHideSummaryBody && (
                                            <Divider sx={{ mb: 3 }}>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    endIcon={isSummaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    onClick={() => {
                                                        if (isSummaryExpanded) {
                                                            setExpandedSummaryReports((prev) => ({
                                                                ...prev,
                                                                [reportSummaryKey]: false
                                                            }));
                                                            setHeaderOnlySummaryReports((prev) => ({
                                                                ...prev,
                                                                [reportSummaryKey]: true
                                                            }));
                                                        } else {
                                                            setExpandedSummaryReports((prev) => ({
                                                                ...prev,
                                                                [reportSummaryKey]: true
                                                            }));
                                                            setHeaderOnlySummaryReports((prev) => ({
                                                                ...prev,
                                                                [reportSummaryKey]: false
                                                            }));
                                                        }
                                                    }}
                                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                                >
                                                    {isSummaryExpanded ? 'Hide summary' : 'See full summary'}
                                                </Button>
                                            </Divider>
                                        )}
                                    </>
                                )}

                                {/* Medical Chronology Table */}
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2
                                }}>
                                    < Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'var(--text-primary)' }}>
                                        Medical Chronology
                                    </Typography>
                                    <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Chronology" />
                                </Box>
                                {renderConsolidatedChronologyTable(report.medical_chronology, reportIndex)}
                            </AccordionDetails >
                        </Accordion >
                    );
                })
                }
            </Box >
        );
    }

    function hasSummaryTableRows(report) {
        const preExistingConditionsList = report.pre_existing_conditions_list || [];
        const injuriesSummary = report.assessment_diagnosis_list || [];
        const treatmentsSummary = report.plan_treatment_rendered_list || [];

        return preExistingConditionsList.length > 0
            || injuriesSummary.length > 0
            || treatmentsSummary.length > 0;
    }

    function getSummaryTableRows(report, reportIndex) {
        const rows = [];
        const reportBase = `$.analysis.medical_reports[${reportIndex}]`;

        // Row 1: Pre-existing conditions
        const preExistingConditionsList = report.pre_existing_conditions_list || [];
        if (preExistingConditionsList.length > 0) {
            preExistingConditionsList.forEach((item, index) => {
                rows.push({
                    description: index === 0 ? 'Pre-existing Conditions' : '',
                    sources: item.sources || [],
                    fieldPaths: buildWarningPaths(`${reportBase}.pre_existing_conditions_list[${index}]`, 'pre_existing_conditions_mentioned'),
                    isFirstInGroup: index === 0,
                    groupCount: preExistingConditionsList.length
                });
            });
        } else {
            rows.push({
                description: 'Pre-existing Conditions',
                placeholder: 'None found',
                sources: [],
                fieldPaths: buildWarningPaths(`${reportBase}.pre_existing_conditions_list[0]`, 'pre_existing_conditions_mentioned'),
                isFirstInGroup: true,
                groupCount: 1
            });
        }

        // Row 2: Injuries as a result of incident
        const injuriesSummary = report.assessment_diagnosis_list || [];
        if (injuriesSummary.length > 0) {
            injuriesSummary.forEach((injury, index) => {
                rows.push({
                    description: index === 0 ? 'Injuries as a Result of Incident' : '',
                    sources: injury.sources || [],
                    fieldPaths: buildWarningPaths(`${reportBase}.assessment_diagnosis_list[${index}]`, 'assessment_diagnosis'),
                    isFirstInGroup: index === 0,
                    groupCount: injuriesSummary.length
                });
            });
        } else {
            rows.push({
                description: 'Injuries as a Result of Incident',
                placeholder: 'None found',
                sources: [],
                fieldPaths: buildWarningPaths(`${reportBase}.assessment_diagnosis_list[0]`, 'assessment_diagnosis'),
                isFirstInGroup: true,
                groupCount: 1
            });
        }

        // Row 3: Treatments rendered
        const treatmentsSummary = report.plan_treatment_rendered_list || [];
        if (treatmentsSummary.length > 0) {
            treatmentsSummary.forEach((treatment, index) => {
                rows.push({
                    description: index === 0 ? 'Treatments Rendered' : '',
                    sources: treatment.sources || [],
                    fieldPaths: buildWarningPaths(`${reportBase}.plan_treatment_rendered_list[${index}]`, 'plan_treatment_rendered'),
                    isFirstInGroup: index === 0,
                    groupCount: treatmentsSummary.length
                });
            });
        } else {
            rows.push({
                description: 'Treatments Rendered',
                placeholder: 'None found',
                sources: [],
                fieldPaths: buildWarningPaths(`${reportBase}.plan_treatment_rendered_list[0]`, 'plan_treatment_rendered'),
                isFirstInGroup: true,
                groupCount: 1
            });
        }

        return rows;
    }

    function renderSummaryTableDataRows(rows) {
        return rows.map((row, index) => {
            return (
                <TableRow
                    key={index}
                    sx={{
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    {row.isFirstInGroup && (
                        <TableCell
                            rowSpan={row.groupCount}
                            sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '1px solid var(--border-color)',
                                verticalAlign: 'top',
                                paddingTop: '16px'
                            }}
                        >
                            {row.description}
                        </TableCell>
                    )}
                    <TableCell sx={{
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...row.fieldPaths}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                if (row.sources.length > 0) {
                                    const firstSource = row.sources[0];
                                    setDisplayedDocument({
                                        'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                        'page': firstSource?.page || 1
                                    });
                                }
                            }}
                            placeholder={row.placeholder}
                        />
                    </TableCell>
                    <TableCell sx={{
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        {row.sources.length > 0 ? (
                            <>
                                {row.sources.map((source, sourceIndex) => (
                                    <Box key={sourceIndex} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography
                                            component="span"
                                            title="Click to view source document"
                                            sx={{
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontSize: '0.9rem',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': getJSONPathCompatibleKey(source?.filename),
                                                    'page': source?.page
                                                });
                                            }}
                                        >
                                            {source?.filename} {source?.page && `(page ${source?.page})`}{source?.bates_number && `(${source?.bates_number})`}
                                        </Typography>
                                        <IconButton
                                            className="source-view-icon"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': getJSONPathCompatibleKey(source?.filename),
                                                    'page': source?.page
                                                });
                                            }}
                                            sx={{
                                                padding: '2px',
                                                visibility: 'hidden',
                                                opacity: 0,
                                                transition: 'opacity 0.2s, visibility 0.2s',
                                                '&:hover': {
                                                    opacity: 1,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                            title="Click to view source document"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Box>
                                )).reduce((prev, curr, index) => [
                                    ...prev,
                                    index > 0 && (
                                        <Typography
                                            key={`comma-${index}`}
                                            component="span"
                                            sx={{
                                                mx: 0.5,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            <br />
                                        </Typography>
                                    ),
                                    curr
                                ], [])}
                            </>
                        ) : (
                            'N/A'
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    }

    function renderSourcesList(sources) {
        if (!sources || sources.length === 0) {
            return 'N/A';
        }
        return (
            <Box>
                {sources.map((source, index) => (
                    <Box key={index} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            component="span"
                            title="Click to view source document"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '0.9rem',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename),
                                    'page': source?.page
                                });
                            }}
                        >
                            {source?.filename} {source?.page && `(page ${source?.page})`}{source?.bates_number && `(${source?.bates_number})`}
                        </Typography>
                        <IconButton
                            className="source-view-icon"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename),
                                    'page': source?.page
                                });
                            }}
                            sx={{
                                padding: '2px',
                                visibility: 'hidden',
                                opacity: 0,
                                transition: 'opacity 0.2s, visibility 0.2s',
                                '&:hover': {
                                    opacity: 1,
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                            title="Click to view source document"
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        );
    }

    function renderConsolidatedChronologyTable(chronologyItems, reportIndex) {
        if (!chronologyItems || chronologyItems.length === 0) {
            return (
                <Typography sx={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    No chronology items found. Upload documents such as medical records, EOBs, itemized bills, receipts, ledgers, progress notes, etc to extract medical chronology.
                </Typography>
            );
        }

        // Preserve original index for path-based edits after sorting.
        const sortedItems = chronologyItems
            .map((chronologyItem, originalIndex) => ({ chronologyItem, originalIndex }))
            .sort((left, right) => {
                const a = chronologyTreatmentStartSortMs(left.chronologyItem);
                const b = chronologyTreatmentStartSortMs(right.chronologyItem);
                if (Number.isNaN(a) && Number.isNaN(b)) return 0;
                if (Number.isNaN(a)) return 1;
                if (Number.isNaN(b)) return -1;
                return a - b;
            });

        return (
            <Table
                ref={tableRef}
                sx={{
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "120px"
                        }}>
                            Treatment Dates
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "200px"
                        }}>
                            Provider
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "300px"
                        }}>
                            Summary
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "200px"
                        }}>
                            Event Details
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "200px"
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedItems.map(({ chronologyItem, originalIndex }) => {
                        const chronologyBasePath = `$.analysis.medical_reports[${reportIndex}].medical_chronology[${originalIndex}]`;
                        const sources = chronologyItem.sources || [];
                        const handleWarningClick = () => {
                            if (sources.length > 0) {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(sources[0]?.filename || ''),
                                    'page': sources[0]?.page || 1
                                });
                            }
                        };
                        return (
                            <TableRow
                                key={chronologyItem.uuid || originalIndex}
                                sx={{
                                    '&:hover .source-view-icon': {
                                        visibility: 'visible',
                                        opacity: 0.7
                                    }
                                }}
                            >
                                <TableCell sx={{
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderConsolidatedTreatmentDates(chronologyItem, reportIndex, originalIndex)}
                                </TableCell>
                                <TableCell sx={{
                                    color: 'var(--text-primary)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderProviders(chronologyItem, '', originalIndex, true, reportIndex)}
                                </TableCell>
                                <TableCell sx={{
                                    color: 'var(--text-primary)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    <EditableFieldWithWarnings
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        {...buildWarningPaths(chronologyBasePath, 'summary')}
                                        formatValue={getFormatFunction('summary')}
                                        setSnackBarMessage={setSnackBarMessage}
                                        onWarningClick={handleWarningClick}
                                    />
                                </TableCell>
                                <TableCell sx={{
                                    color: 'var(--text-primary)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderEventDetails(chronologyItem, chronologyBasePath, handleWarningClick)}
                                </TableCell>
                                <TableCell sx={{
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderSourcesList(chronologyItem.sources || [])}
                                </TableCell>
                            </TableRow >
                        );
                    })}
                </TableBody>
            </Table >
        );
    }

    function renderEventDetails(chronologyItem, chronologyBasePath, handleWarningClick) {
        const details = [];

        if (chronologyItem.detailed_narrative && Array.isArray(chronologyItem.detailed_narrative)) {
            chronologyItem.detailed_narrative.forEach((narrativeItem, eventIndex) => {
                if (!narrativeItem.value) return;
                const label = getNarrativeItemTypeLabel(narrativeItem.item_type);
                details.push(
                    <Box key={`${narrativeItem.item_type}-${eventIndex}`} sx={{ mb: 1 }}>
                        <Box>
                            <strong>{label}:</strong>
                            <Box component="span" onClick={(e) => e.stopPropagation()}>
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    {...buildWarningPaths(`${chronologyBasePath}.detailed_narrative[${eventIndex}]`, 'value')}
                                    isEventDetail={true}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            });
        }

        return details.length > 0 ? (
            <span>
                {details.map((detail, index) => (
                    <span key={index}>
                        {detail}
                        {index < details.length - 1 && <br />}
                    </span>
                ))}
            </span>
        ) : '';
    }

    function medicalChronology() {
        // Get document-based medical chronology (All Records)
        var documentKeysToDisplay = getSelectedDocumentKeys();
        var medicalChronology = documentKeysToDisplay.map(documentKey => {
            caseInfo['documents'][documentKey]['analysis']?.['medical_chronology']?.forEach((chronologyItem, i) => {
                // Add null check to prevent errors
                if (chronologyItem) {
                    chronologyItem['id'] = documentKey + i; // Used in UI as keys for use by React framework
                    chronologyItem['source_filekey'] = documentKey; // File used by SourceDocumentViewer
                }
            });
            return caseInfo['documents'][documentKey]['analysis']?.['medical_chronology'];
        })
            .filter(chronologyArr => chronologyArr) // the ?. above can cause null entries; filter them
            .reduce((accumulator, chronologyArr) => accumulator.concat(chronologyArr), [])
            .filter(chronologyItem => isValidMedicalChronologyItem(chronologyItem)) // Remove '', 'N/A', null, etc
            .sort((item1, item2) => {
                const a = chronologyTreatmentStartSortMs(item1);
                const b = chronologyTreatmentStartSortMs(item2);
                if (Number.isNaN(a) && Number.isNaN(b)) return 0;
                if (Number.isNaN(a)) return 1;
                if (Number.isNaN(b)) return -1;
                return a - b;
            });

        if (medicalChronology.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    {renderAuditWidgetForAllRecords()}
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        Medical chronology will be automatically extracted from uploaded medical records and listed here.
                    </Typography>
                </Box>
            )
        }

        var tableBodyRows = medicalChronology
            .map(chronologyItem => {
                // Find the document key for this chronology item
                const documentKey = chronologyItem.source_filekey;
                const docInfo = caseInfo.documents[documentKey];
                const filename = docInfo?.['filename'];
                const itemIndex = caseInfo.documents[documentKey]?.analysis?.medical_chronology?.findIndex(item => item.id === chronologyItem.id) || 0;
                const chronologyBasePath = `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}]`;
                const handleWarningClick = (e) => {
                    e?.stopPropagation?.();
                    setDisplayedDocument({
                        'key': documentKey,
                        'page': chronologyItem.source?.page || 1
                    });
                };

                return (
                    <TableRow
                        key={chronologyItem['id']}
                        className={chronologyItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}
                    >
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            fontWeight: 500
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={handleWarningClick}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'patient_name')}
                                formatValue={getFormatFunction('patient')}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ fontWeight: 500 }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            {renderChronologyTreatmentDates(chronologyItem, documentKey, itemIndex)}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '200px'
                        }}>
                            {renderProviders(chronologyItem, documentKey, itemIndex, false)}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '300px'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={handleWarningClick}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'summary')}
                                formatValue={getFormatFunction('summary')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '300px'
                        }}>
                            {renderEventDetails(chronologyItem, chronologyBasePath, handleWarningClick)}
                        </TableCell>
                        <TableCell sx={{
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': chronologyItem['source_filekey'],
                                            'page': chronologyItem.source?.page || 1
                                        });
                                    }}
                                >
                                    {filename} {chronologyItem.source?.page && `(page ${chronologyItem.source?.page})`}{chronologyItem.source?.bates_number && `(${chronologyItem.source?.bates_number})`}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': chronologyItem['source_filekey'],
                                            'page': chronologyItem.source?.page || 1
                                        });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            });

        return (
            <Box>
                {renderAuditWidgetForAllRecords()}
                <Table
                    ref={tableRef}
                    sx={{
                        minWidth: 650,
                        backgroundColor: 'var(--background-color)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid var(--border-color)',
                        boxShadow: 'var(--shadow-light)'
                    }}
                    aria-label="medical chronology table"
                >
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                            <TableCell sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '2px solid var(--border-color)',
                                padding: '16px 12px',
                                minWidth: "100px"
                            }}>
                                Patient
                            </TableCell>
                            <TableCell sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '2px solid var(--border-color)',
                                padding: '16px 12px',
                                minWidth: "120px"
                            }}>
                                Treatment Dates
                            </TableCell>
                            <TableCell sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '2px solid var(--border-color)',
                                padding: '16px 12px',
                                minWidth: "200px"
                            }}>
                                Facility/Provider
                            </TableCell>
                            <TableCell sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '2px solid var(--border-color)',
                                padding: '16px 12px',
                                minWidth: "300px"
                            }}>
                                Summary
                            </TableCell>
                            <TableCell sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '2px solid var(--border-color)',
                                padding: '16px 12px',
                                minWidth: "600px"
                            }}>
                                Event Details
                            </TableCell>
                            <TableCell sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '2px solid var(--border-color)',
                                padding: '16px 12px'
                            }}>
                                Source Document
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableBodyRows}
                    </TableBody>
                </Table>
            </Box>
        );
    }

    function renderChronologyTreatmentDates(chronologyItem, documentKey, itemIndex) {
        const baseFieldPath = `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument, documentKey);
    }

    function renderConsolidatedTreatmentDates(chronologyItem, reportIndex, itemIndex) {
        const baseFieldPath = `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument);
    }


    function isValidMedicalChronologyItem(chronologyItem) {
        return chronologyItem && (chronologyItem['patient_name'] || chronologyItem['providers']?.length > 0 || chronologyItem['treatment_start_date']);
    }


    /**
     * Renders provider information for a chronology item (handles both old and new schemas)
     * @param {Object} chronologyItem - The chronology item
     * @param {string} documentKey - Key of the document (for non-consolidated view)
     * @param {number} itemIndex - Index of the chronology item
     * @param {boolean} isConsolidated - Whether this is the consolidated view
     * @param {number} reportIndex - Index of the medical report (for consolidated view)
     * @returns {JSX.Element} The rendered provider information
     */
    function renderProviders(chronologyItem, documentKey, itemIndex, isConsolidated = false, reportIndex = 0) {
        const hasProvidersArray = Array.isArray(chronologyItem.providers) && chronologyItem.providers.length > 0;
        const hasOldSchema = chronologyItem.provider_name || chronologyItem.provider_type;
        const basePath = isConsolidated
            ? `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}]`
            : `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}]`;
        const handleWarningClick = () => {
            if (isConsolidated) {
                const sourcesArray = chronologyItem.sources || [];
                if (sourcesArray.length > 0) {
                    setDisplayedDocument({
                        'key': getJSONPathCompatibleKey(sourcesArray[0]?.filename || ''),
                        'page': sourcesArray[0]?.page || 1
                    });
                }
            } else {
                setDisplayedDocument({
                    'key': documentKey,
                    'page': chronologyItem.source?.page || 1
                });
            }
        };

        return (
            <Box>
                {hasProvidersArray ? (
                    <>
                        <strong>Provider:</strong>
                        <Box sx={{ mb: 1 }}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildWarningPaths(`${basePath}.providers[0]`, 'provider_name')}
                                setSnackBarMessage={setSnackBarMessage}
                                onWarningClick={handleWarningClick}
                            />
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                textPath={`${basePath}.providers[0].provider_type`}
                                formatValue={getFormatFunction('provider_type')}
                                setSnackBarMessage={setSnackBarMessage}
                                onWarningClick={handleWarningClick}
                            />
                        </Box>
                        {(chronologyItem.facility_name || chronologyItem.facility_type) && (
                            <Box sx={{ mb: 1, ml: 0 }}>
                                <strong>Facility:</strong>
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    {...buildWarningPaths(basePath, 'facility_name')}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    textPath={`${basePath}.facility_type`}
                                    formatValue={getFormatFunction('facility_type')}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                            </Box>
                        )}
                        {chronologyItem.providers.length > 1 && chronologyItem.providers.slice(1).map((provider, providerIndex) => (
                            <Box key={providerIndex + 1} sx={{ mb: 1, mt: 1 }}>
                                {providerIndex === 0 && <strong>Other Providers:</strong>}
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    {...buildWarningPaths(`${basePath}.providers[${providerIndex + 1}]`, 'provider_name')}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    {...buildWarningPaths(`${basePath}.providers[${providerIndex + 1}]`, 'provider_type')}
                                    formatValue={getFormatFunction('provider_type')}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                            </Box>
                        ))}
                    </>
                ) : hasOldSchema ? (
                    <>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildWarningPaths(basePath, 'provider_name')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={handleWarningClick}
                        />
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildWarningPaths(basePath, 'provider_type')}
                            formatValue={getFormatFunction('provider_type')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={handleWarningClick}
                        />
                    </>
                ) : null
                }
            </Box >
        );
    }

}

// TODO: Move to helper
function assert(condition, message) {
    if (!condition) {
        throw new Error(`Invariant failed: ${message || "Condition not met."}`);
    }
}
